import type { Table as TanStackTable } from '@tanstack/table-core';
import { TableRowSelectionMode } from '../types';
import type { TableRecord, TableRowState, TableNode } from '../types';
import type { SelectionManagerBase } from './selection-managers/selection-manager-base';
import { DisabledSelectionManager } from './selection-managers/disabled-selection-manager';
import { MultiSelectionManager } from './selection-managers/multi-selection-manager';
import { SingleSelectionManager } from './selection-managers/single-selection-manager';

/**
 * Manages the behavior associated with interactive row selection for the table, including
 * handling when the selection mode of the table is changed.
 */
export class InteractiveSelectionManager<TData extends TableRecord> {
    private readonly tanStackTable: TanStackTable<TableNode<TData>>;
    private selectionManager: SelectionManagerBase<TData>;
    private actionMenusPreserveSelection: boolean;

    public constructor(
        tanStackTable: TanStackTable<TableNode<TData>>,
        selectionMode: TableRowSelectionMode,
        actionMenusPreserveSelection: boolean
    ) {
        this.tanStackTable = tanStackTable;
        this.actionMenusPreserveSelection = actionMenusPreserveSelection;
        this.selectionManager = this.createSelectionManager(
            selectionMode,
            actionMenusPreserveSelection
        );
    }

    public handleRowSelectionToggle(
        rowState: TableRowState | undefined,
        isSelecting: boolean,
        shiftKey: boolean
    ): boolean {
        if (!rowState) {
            return false;
        }

        return this.selectionManager.handleRowSelectionToggle(
            rowState,
            isSelecting,
            shiftKey
        );
    }

    public handleRowClick(
        rowState: TableRowState | undefined,
        shiftKey: boolean,
        ctrlKey: boolean
    ): boolean {
        if (!rowState) {
            return false;
        }

        return this.selectionManager.handleRowClick(
            rowState,
            shiftKey,
            ctrlKey
        );
    }

    public handleActionMenuOpening(
        rowState: TableRowState | undefined
    ): boolean {
        if (!rowState) {
            return false;
        }

        return this.selectionManager.handleActionMenuOpening(rowState);
    }

    public handleSelectionModeChanged(
        selectionMode: TableRowSelectionMode
    ): void {
        this.selectionManager = this.createSelectionManager(
            selectionMode,
            this.actionMenusPreserveSelection
        );
    }

    public handleActionMenusPreserveSelectionChanged(
        actionMenusPreserveSelection: boolean
    ): void {
        this.actionMenusPreserveSelection = actionMenusPreserveSelection;

        this.selectionManager.updateActionMenusPreserveSelection(
            actionMenusPreserveSelection
        );
    }

    public handleSelectionReset(): void {
        this.selectionManager.reset();
    }

    public getCurrentSelectedRecordIds(): string[] {
        const tanStackSelectionState = this.tanStackTable.options.state.rowSelection;
        if (!tanStackSelectionState) {
            return [];
        }

        const selectedRecordIds: string[] = [];
        Object.entries(tanStackSelectionState).forEach(
            ([recordId, isSelected]) => {
                if (isSelected) {
                    selectedRecordIds.push(recordId);
                }
            }
        );
        return selectedRecordIds;
    }

    private createSelectionManager(
        selectionMode: TableRowSelectionMode,
        actionMenusPreserveSelection: boolean
    ): SelectionManagerBase<TData> {
        switch (selectionMode) {
            case TableRowSelectionMode.multiple:
                return new MultiSelectionManager(
                    this.tanStackTable,
                    actionMenusPreserveSelection
                );
            case TableRowSelectionMode.single:
                return new SingleSelectionManager(
                    this.tanStackTable,
                    actionMenusPreserveSelection
                );
            case TableRowSelectionMode.none:
                return new DisabledSelectionManager(
                    this.tanStackTable,
                    actionMenusPreserveSelection
                );
            default:
                throw new Error('unknown selection mode found');
        }
    }
}
